import { Controller } from "@hotwired/stimulus";

/**
 * Message Review Controller
 *
 * Provides on-demand message review via a "Review Message" button.
 * Sends the message to the review API which enqueues an async job.
 * Results are delivered via Turbo Stream broadcast.
 */
export default class extends Controller {
  static targets = ["input", "button", "resultPanel"];
  static values = {
    url: String,
    enabled: Boolean,
    channel: { type: String, default: "all" },
    mode: { type: String, default: "sms_grade" },
    minLength: { type: Number, default: 10 },
    notificationId: Number,
    timeout: { type: Number, default: 30000 },
  };

  static reviewSteps = ["Source", "Hazard", "Impacts", "Location", "Guidance", "Time"];

  connect() {
    this.abortController = null;
    this.loadingInterval = null;
    this.timeoutId = null;
    this.isReviewing = false;
    this.updateButtonState();

    // Listen for Turbo Stream renders to detect when results arrive
    this.boundHandleTurboRender = this.handleTurboRender.bind(this);
    document.addEventListener("turbo:before-stream-render", this.boundHandleTurboRender);
  }

  disconnect() {
    this.cancelPendingRequest();
    this.stopLoadingAnimation();
    this.clearTimeout();
    document.removeEventListener("turbo:before-stream-render", this.boundHandleTurboRender);
  }

  inputTargetConnected() {
    this.boundUpdateButtonState = () => this.updateButtonState();
    this.inputTarget.addEventListener("input", this.boundUpdateButtonState);
    this.inputTarget.addEventListener("change", this.boundUpdateButtonState);
  }

  inputTargetDisconnected() {
    this.inputTarget.removeEventListener("input", this.boundUpdateButtonState);
    this.inputTarget.removeEventListener("change", this.boundUpdateButtonState);
  }

  updateButtonState() {
    if (!this.enabledValue) return;
    if (!this.hasButtonTarget || !this.hasInputTarget) return;

    const length = this.inputTarget.value.trim().length;
    // Don't enable button while reviewing is in progress
    this.buttonTarget.disabled = length < this.minLengthValue || this.isReviewing;
  }

  handleTurboRender(event) {
    // Check if this Turbo Stream targets our review panel
    const target = event.target;
    if (!target) return;

    const targetId = target.getAttribute("target");
    const expectedTargetId = `message_review_panel_${this.notificationIdValue}`;

    if (targetId === expectedTargetId) {
      // Results have arrived, stop loading state
      this.onResultsReceived();
    }
  }

  onResultsReceived() {
    this.isReviewing = false;
    this.setLoading(false);
    this.clearTimeout();
  }

  async grade(event) {
    event.preventDefault();

    const message = this.inputTarget.value.trim();
    if (message.length < this.minLengthValue) return;

    this.cancelPendingRequest();
    this.isReviewing = true;
    this.setLoading(true);

    this.abortController = new AbortController();

    // Set timeout as safety net in case broadcast fails
    this.startTimeout();

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({
          message: message,
          channel: this.channelValue,
          notification_id: this.notificationIdValue,
          mode: this.modeValue,
        }),
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Check if job was enqueued successfully
      if (result.status !== "processing") {
        throw new Error(result.error || "Unexpected response");
      }

      // Results will arrive via Turbo Stream broadcast
      // Loading state continues until handleTurboRender fires or timeout
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Message review error:", error);
      this.showError("Unable to review message. Please try again.");
      this.isReviewing = false;
      this.setLoading(false);
      this.clearTimeout();
    }
  }

  startTimeout() {
    this.clearTimeout();
    this.timeoutId = setTimeout(() => {
      if (this.isReviewing) {
        this.showError("Review is taking longer than expected. Please try again.");
        this.isReviewing = false;
        this.setLoading(false);
      }
    }, this.timeoutValue);
  }

  clearTimeout() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  setLoading(loading) {
    if (!this.hasButtonTarget) return;

    this.buttonTarget.disabled = loading;

    if (loading) {
      this.startLoadingAnimation();
    } else {
      this.stopLoadingAnimation();
      this.buttonTarget.innerHTML = '<i class="check circle icon"></i> Review Message';
    }
  }

  startLoadingAnimation() {
    this.currentStep = 0;
    this.updateLoadingText();
    this.loadingInterval = setInterval(() => {
      this.currentStep = (this.currentStep + 1) % this.constructor.reviewSteps.length;
      this.updateLoadingText();
    }, 2500);
  }

  stopLoadingAnimation() {
    if (this.loadingInterval) {
      clearInterval(this.loadingInterval);
      this.loadingInterval = null;
    }
  }

  updateLoadingText() {
    if (!this.hasButtonTarget) return;
    const step = this.constructor.reviewSteps[this.currentStep];
    this.buttonTarget.innerHTML = `<i class="spinner loading icon"></i> Reviewing ${step}...`;
  }

  showError(message) {
    if (this.hasResultPanelTarget) {
      // Find or create error display area within the panel
      let errorDiv = this.resultPanelTarget.querySelector(".ui.error.message");
      if (!errorDiv) {
        errorDiv = document.createElement("div");
        errorDiv.className = "ui error message";
        // Insert after the header
        const header = this.resultPanelTarget.querySelector(".ui.header");
        if (header && header.nextSibling) {
          header.parentNode.insertBefore(errorDiv, header.nextSibling);
        } else {
          this.resultPanelTarget.appendChild(errorDiv);
        }
      }
      errorDiv.innerHTML = `<i class="exclamation triangle icon"></i> ${this.escapeHtml(message)}`;
    }
  }

  useSuggestion(event) {
    event.preventDefault();
    const rewriteText = event.currentTarget.dataset.rewrite;
    if (rewriteText && this.hasInputTarget) {
      this.inputTarget.value = rewriteText;
      this.triggerInputEvents();
    }
  }

  triggerInputEvents() {
    // Dispatch multiple events to trigger various listeners (character counter uses keyup/change)
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
    this.inputTarget.dispatchEvent(new KeyboardEvent("keyup", { bubbles: true }));
  }

  cancelPendingRequest() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") : "";
  }
}
